import { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useNavigate } from "react-router";
import { cancelApplication } from "../../services/AdminDashService";
import { Application } from "../../types/Application";
import { Applicant } from "../../types/Applicant";
import Select from "react-select";
import ErrorIcon from "../Shared/icons/ErrorIcon";

interface Props {
  selectedApplicant: Applicant | undefined;
  selectedApplication: Application;
  isHighRiskApplication: boolean;
  onCancel(): any;
  show?: boolean;
  type?: string;
}

const CancelApplicationModal = ({
  selectedApplicant,
  selectedApplication,
  isHighRiskApplication = false,
  onCancel,
  show,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const notification = useAppSelector(
    (state) => state.applications.notification
  );
  const [cancelling, setCancelling] = useState<boolean>(false);
  const cancelReasonOptions = [
    { value: "high-risk", label: "High Risk" },
    { value: "standard", label: "Standard" },
  ];
  const [cancelReason, setCancelReason] = useState<{
    value: string;
    label: string;
  } | null>(null);
  //const [showReasonError, setShowReasonError] = useState<boolean>(false);

  const DropdownIndicator = (props: any) => {
    return (
      <div
        {...props.innerProps}
        style={{ padding: "0 8px", display: "flex", alignItems: "center" }}
      >
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 6L0 0H10L5 6Z" fill="#666" />
        </svg>
      </div>
    );
  };

  const cancelAction = () => {
    onCancel();
  };

  useEffect(() => {
    if (notification && notification?.origin === "cancelApplication") {
      setCancelling(false);
      if (
        notification &&
        notification.type === "INFO" &&
        notification.message?.endsWith("has been successfully cancelled")
      ) {
        navigate("/AdminDashboard/Applications");
      } else {
        onCancel();
      }
    }
  }, [notification]);

  const cancelApplicationAction = () => {
    if (isHighRiskApplication && !cancelReason) {
      return;
    }
    setCancelling(true);
    dispatch(
      cancelApplication(
        selectedApplication.applicationId,
        selectedApplication.unitId,
        isHighRiskApplication ? -4 : undefined // -4 indicates high-risk cancellation
      )
    );
  };

  const RenderCancelApp = () => {
    return (
      <div className="details">
        <div className="row">
          <div className="col form">
            <label>Application:</label>
          </div>
          <div className="col">
            <div>
              {selectedApplication && selectedApplication.communityName}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col form">
            <label>Primary Applicant:</label>
          </div>
          <div className="col">
            <div>
              {selectedApplicant &&
                `${selectedApplicant.firstName}${
                  selectedApplicant.middleName
                    ? " " + selectedApplicant.middleName + " "
                    : " "
                }${selectedApplicant.lastName}`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RenderHighRiskCancelApp = () => {
    return (
      <div className="high-risk-cancel-details">
        <p className="cancel-question">
          Are you cancelling this application due to High Risk or Standard
          cancellation?
        </p>
        <div className="reason-section">
          <fieldset className="reason-fieldset">
            <legend className="reason-label">Select reason</legend>
            <div className="dropdown-cancel-reason">
              <Select
                placeholder="Options"
                instanceId="cancel-reason"
                value={cancelReason}
                options={cancelReasonOptions}
                components={{ DropdownIndicator }}
                onChange={(option) => {
                  setCancelReason(option);
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "none",
                    boxShadow: "none",
                    borderColor: base.borderColor,
                    "&:hover": {
                      borderColor: base.borderColor,
                    },
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
              />
            </div>
          </fieldset>
        </div>
      </div>
    );
  };

  return (
    <Modal aria-labelledby="contained-modal-title-vcenter" centered show={show}>
      <Modal.Header>
        <Modal.Title>
          {!isHighRiskApplication ? (
            <>
              <span className="cancel-icon">!</span>Cancel Application
            </>
          ) : (
            <>
              <ErrorIcon /> Confirm cancellation
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isHighRiskApplication ? (
          <RenderHighRiskCancelApp />
        ) : (
          <RenderCancelApp />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="cancel-btn-high-risk"
          variant="secondary"
          onClick={cancelAction}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={cancelApplicationAction}
          disabled={cancelling || (isHighRiskApplication && !cancelReason)}
          id="cancel-action"
        >
          {cancelling && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
          )}
          {cancelling
            ? "Cancelling Application"
            : isHighRiskApplication
            ? "Submit Cancellation"
            : "Cancel Application"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelApplicationModal;
